import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Pause, Play, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { useConfirm } from "@/components/ConfirmDialog";
import { CountdownCell } from "@/components/CountdownCell";
import { RefBadge } from "@/components/RefBadge";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

export default function SchedulesPage({ projectId = null, embedded = false }) {
  const confirm = useConfirm();
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (projectId) params.set("project_id", projectId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/schedules${qs}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [statusFilter, projectId]);

  useEffect(load, [load]);

  const handleCancel = async (s) => {
    const ok = await confirm({
      title: "Cancel this schedule?",
      description: `Future runs of "${s.entity_name}" via this schedule will stop. The schedule will be marked Cancelled (kept in the list for audit). You can create a new schedule any time.`,
      confirmLabel: "Cancel schedule",
      danger: true,
      testId: "confirm-cancel-schedule",
    });
    if (!ok) return;
    setBusyId(s.id);
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success("Schedule cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handlePause = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/schedules/${s.id}/pause`);
      toast.success("Schedule paused");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleResume = async (s) => {
    setBusyId(s.id);
    try {
      const { data } = await api.post(`/schedules/${s.id}/resume`);
      toast.success(data?.status === "completed" ? "Schedule resumed — already past due, marked completed" : "Schedule resumed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: "entity",
      label: "Test / Suite",
      mobileVariant: "title",
      render: (s) => (
        <div className="flex flex-col">
          <RefBadge value={s.entity_ref} testId={`sched-entity-ref-${s.id}`} />
          <span className="rt-primary mt-1" data-testid={`sched-name-${s.id}`}>
            {s.entity_name || s.entity_id}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-[#94A3B8] mt-0.5">
            {s.entity_type}
          </span>
        </div>
      ),
    },
    {
      key: "recurrence",
      label: "Recurrence",
      render: (s) => (
        <div className="flex flex-col">
          <span className="text-sm text-[#0F172A] capitalize" data-testid={`sched-recurrence-${s.id}`}>
            {s.recurrence_type}
          </span>
          <span className="text-[11px] text-[#64748B]">
            {s.recurrence_type === "once"
              ? s.run_date_local
              : (s.run_time_local || "")}
            {s.recurrence_type === "weekly" && s.weekdays?.length > 0 && (
              <> · {s.weekdays.map((d) => WEEKDAY_LABELS[d]).filter(Boolean).join(", ")}</>
            )}
            {" · "}{s.timezone}
          </span>
        </div>
      ),
    },
    {
      key: "next_run",
      label: "Next run at",
      render: (s) => (
        <span className="text-xs text-[#475569]" data-testid={`sched-next-${s.id}`}>
          {s.next_run_at_utc ? formatDateTime(s.next_run_at_utc) : "—"}
        </span>
      ),
    },
    {
      key: "countdown",
      label: "Time left",
      render: (s) => <CountdownCell targetIso={s.next_run_at_utc} status={s.status} />,
    },
    {
      key: "status",
      label: "Status",
      render: (s) => {
        const color =
          s.status === "active" ? "text-[#0F766E] bg-[#ECFDF5] border-[#A7F3D0]"
          : s.status === "paused" ? "text-[#92400E] bg-[#FEF3C7] border-[#FCD34D]"
          : s.status === "completed" ? "text-[#475569] bg-[#F1F5F9] border-[#CBD5E1]"
          : "text-[#94A3B8] bg-[#F8FAFC] border-[#E5E7EB]";
        return (
          <span
            data-testid={`sched-status-${s.id}`}
            className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full border ${color}`}
          >
            {s.status}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (s) => (
        <div className="flex items-center justify-end gap-1">
          {s.status === "active" && (
            <button
              onClick={() => handlePause(s)}
              disabled={busyId === s.id}
              data-testid={`sched-pause-${s.id}`}
              aria-label="Pause"
              title="Pause this schedule"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B45309] hover:border-[#FCD34D] hover:bg-[#FFFBEB] disabled:opacity-50"
            >
              {busyId === s.id ? <Loader2 size={13} className="animate-spin" /> : <Pause size={13} />}
            </button>
          )}
          {s.status === "paused" && (
            <button
              onClick={() => handleResume(s)}
              disabled={busyId === s.id}
              data-testid={`sched-resume-${s.id}`}
              aria-label="Resume"
              title="Resume this schedule"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#047857] hover:border-[#A7F3D0] hover:bg-[#ECFDF5] disabled:opacity-50"
            >
              {busyId === s.id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            </button>
          )}
          {(s.status === "active" || s.status === "paused") && (
            <button
              onClick={() => handleCancel(s)}
              disabled={busyId === s.id}
              data-testid={`sched-cancel-${s.id}`}
              aria-label="Cancel"
              title="Cancel this schedule"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2] disabled:opacity-50"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div
      className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10"}
      data-testid={embedded ? "schedules-page-embedded" : "schedules-page"}
    >
      {!embedded && (
        <PageHeader
          title="Schedules"
          description="Every scheduled run across this workspace. Countdown ticks live — switch to Completed or Cancelled to audit history."
          actions={
            <button
              onClick={load}
              data-testid="schedules-refresh"
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md border border-[#E5E7EB] text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
            >
              <RefreshCw size={13} />
              Refresh
            </button>
          }
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="text-sm text-[#64748B] max-w-2xl">
            Schedules for tests and suites in this project. Standalone test cases live on the workspace-level Schedules page.
          </p>
          <button
            onClick={load}
            data-testid="schedules-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#E5E7EB] text-sm font-medium text-[#0F172A] hover:bg-[#F8FAFC] shrink-0"
          >
            <RefreshCw size={13} />
            Refresh
          </button>
        </div>
      )}

      <div className="flex items-center gap-1 p-1 bg-[#F8FAFC] border border-[#E5E7EB] rounded-md w-fit mb-6">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setStatusFilter(f.id)}
            data-testid={`schedules-filter-${f.id}`}
            className={`inline-flex items-center h-8 px-3 rounded-md text-xs font-medium transition-colors ${
              statusFilter === f.id
                ? "bg-[#0F172A] text-white"
                : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="schedules-empty">
          <CalendarClock size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No schedules{statusFilter !== "all" ? ` (${statusFilter})` : ""}</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Schedule a test or suite from its row to see it appear here with a live countdown.
          </p>
        </div>
      ) : (
        <ResponsiveTable
          testId="schedules-table"
          columns={columns}
          rows={items}
          getRowTestId={(s) => `schedule-row-${s.id}`}
        />
      )}
    </div>
  );
}
